import React from "react";
import {
  FaCheck,
  FaChevronRight,
  FaFlagCheckered,
  FaTimes,
} from "react-icons/fa";

function ProgressBar({ value = 0 }) {
  return (
    <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
      <div
        className="h-full rounded-full bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 transition-[width] duration-300"
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

function OptionButton({ text, onClick, state }) {
  const styles = {
    idle: "border-slate-200 bg-white text-slate-900 hover:-translate-y-0.5 hover:border-violet-200 hover:bg-violet-50 hover:shadow-md dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:hover:border-violet-700 dark:hover:bg-violet-950/30",
    correct:
      "border-emerald-300 bg-emerald-50 text-emerald-900 shadow-md shadow-emerald-100 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100 dark:shadow-black/10",
    wrong:
      "border-rose-300 bg-rose-50 text-rose-900 shadow-md shadow-rose-100 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-100 dark:shadow-black/10",
    disabled:
      "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-800 dark:bg-slate-900/70 dark:text-slate-500",
  };

  const icon =
    state === "correct" ? <FaCheck /> : state === "wrong" ? <FaTimes /> : null;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={state === "disabled"}
      className={[
        "group flex min-h-14 w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left text-sm font-bold transition sm:text-base",
        styles[state || "idle"],
      ].join(" ")}
    >
      <span>{text}</span>

      {icon && (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/80 text-sm shadow-sm dark:bg-slate-950/80">
          {icon}
        </span>
      )}
    </button>
  );
}

export default function QuizQuestion({
  question,
  selected,
  onAnswer,
  onNext,
  hasNext,
  current = 0,
  total = 0,
  score = 0,
  progress = 0,
}) {
  if (!question) return null;

  const { image, correctAnswer, options = [], famille } = question;

  const getState = (option) => {
    if (!selected) return "idle";
    if (option === correctAnswer) return "correct";
    if (option === selected && selected !== correctAnswer) return "wrong";
    return "disabled";
  };

  const feedbackLabel = !selected
    ? "Choisis la bonne réponse"
    : selected === correctAnswer
    ? "Bonne réponse"
    : `Réponse attendue : ${correctAnswer}`;

  return (
    <div className="space-y-4">
      <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/60 sm:p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Question {current + 1} / {total}
            </p>

            <p className="mt-1 text-sm font-bold text-slate-950 dark:text-white sm:text-base">
              {feedbackLabel}
            </p>
          </div>

          <div className="rounded-2xl bg-white px-3 py-2 text-right shadow-sm dark:bg-slate-900">
            <p className="text-[0.65rem] font-bold uppercase tracking-[0.14em] text-slate-400">
              Score
            </p>

            <p className="text-base font-bold text-slate-950 dark:text-white">
              {score}
            </p>
          </div>
        </div>

        <ProgressBar value={progress} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)] lg:items-start">
        <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-lg shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-950 dark:shadow-black/10">
          <div className="bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.12),transparent_35%),linear-gradient(180deg,rgba(248,250,252,1),rgba(255,255,255,1))] p-3 dark:bg-none dark:bg-slate-950 sm:p-5">
            <div
              className="mx-auto w-full max-w-[250px] sm:max-w-[340px] md:max-w-[430px] lg:max-w-[520px]"
              style={{ aspectRatio: "551 / 617" }}
            >
              <img
                src={image}
                alt={`Technique ${famille || "judo"}`}
                className="h-full w-full object-contain"
                loading="lazy"
              />
            </div>
          </div>

          <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/70">
            <p className="text-center text-xs font-semibold text-slate-500 dark:text-slate-400 lg:text-left">
              Famille :{" "}
              <span className="text-slate-800 dark:text-slate-100">
                {famille || "Non renseignée"}
              </span>
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="grid gap-2.5">
            {options.map((option) => (
              <OptionButton
                key={option}
                text={option}
                onClick={() => onAnswer?.(option)}
                state={getState(option)}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={onNext}
            disabled={!selected}
            className={[
              "group relative flex min-h-14 w-full items-center justify-center gap-2 overflow-hidden rounded-2xl px-5 py-3 text-sm font-bold text-white shadow-lg transition focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 dark:focus:ring-offset-slate-950 sm:text-base",
              selected
                ? "bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 shadow-violet-200 hover:-translate-y-0.5 hover:shadow-xl dark:shadow-black/20"
                : "cursor-not-allowed bg-slate-300 text-white/80 shadow-none dark:bg-slate-800",
            ].join(" ")}
            title={
              selected
                ? hasNext
                  ? "Question suivante"
                  : "Voir les résultats"
                : "Choisis une réponse"
            }
          >
            {hasNext ? (
              <>
                <span>Question suivante</span>
                <FaChevronRight
                  className={
                    selected
                      ? "transition-transform group-hover:translate-x-0.5"
                      : ""
                  }
                />
              </>
            ) : (
              <>
                <span>Voir les résultats</span>
                <FaFlagCheckered />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}